import { createApp, watch } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';  // Import du router depuis le dossier 'src/router'
import './assets/main.css'

// charts
import VueECharts from 'vue-echarts'
import { setupECharts } from './plugins/echarts-setup'
setupECharts()

import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { setupTelemetry } from './plugins/telemetry';

const appInsightsConnectionString = process.env.VUE_APP_APPINSIGHTS_CONNECTION_STRING;
const appInsights = appInsightsConnectionString
  ? new ApplicationInsights({ config: { connectionString: appInsightsConnectionString, enableAutoRouteTracking: true } })
  : null;

if (appInsights) {
  appInsights.loadAppInsights();
  appInsights.addTelemetryInitializer((envelope) => {
    if (!envelope.baseData) return;
    const properties = envelope.baseData.properties || (envelope.baseData.properties = {});
    properties.sourceRole = 'frontend';
    properties.app = 'webfrontend';
  });
}

// initialize telemetry (clicks, scrolls, session start)
setupTelemetry(appInsights, router);


// i18n
import { createI18n } from 'vue-i18n'
import messages from './locales'
const APP_LOCALE_STORAGE_KEY = 'app_locale'
const browserLang = navigator.language.split('-')[0]
const storedLocale = localStorage.getItem(APP_LOCALE_STORAGE_KEY)
const detectedLocale = messages[storedLocale] ? storedLocale : (messages[browserLang] ? browserLang : 'en')
const i18n = createI18n({
  legacy: false,
  locale: detectedLocale,
  fallbackLocale: 'en',
  messages,
  returnObjects: true
})

const CHILD_NAMES_STORAGE_KEY = 'parent_child_names_by_id';

function getChildNamesById() {
  try {
    return JSON.parse(sessionStorage.getItem(CHILD_NAMES_STORAGE_KEY) || '{}');
  } catch (error) {
    console.warn('Unable to read child names cache', error);
    return {};
  }
}

function getResolvedTitle(route) {
  const routeWithTitle = [...route.matched].reverse().find((record) => record.meta?.titleKey);

  if (!routeWithTitle) {
    return i18n.global.t('pageTitles.home');
  }

  const childId = route.params?.childId;
  const childName = childId ? getChildNamesById()?.[childId] : '';

  if (childName && routeWithTitle.meta?.childTitleKey) {
    return i18n.global.t(routeWithTitle.meta.childTitleKey, { name: childName });
  }

  return i18n.global.t(routeWithTitle.meta?.titleKey || 'pageTitles.home');
}

function updateDocumentTitle(route) {
  const pageTitle = getResolvedTitle(route);
  const appName = i18n.global.t('pageTitles.app');

  document.title = pageTitle === appName ? appName : `${pageTitle} | ${appName}`;
}

router.afterEach((to) => {
  updateDocumentTitle(to);
  appInsights?.trackPageView({ name: to.name || to.path });
});

watch(i18n.global.locale, () => {
  try {
    const locale = i18n.global.locale.value;
    localStorage.setItem(APP_LOCALE_STORAGE_KEY, locale);
  } catch (error) {
    console.warn('Unable to persist locale', error);
  }
  updateDocumentTitle(router.currentRoute.value);
});

window.addEventListener('child-names-updated', () => {
  updateDocumentTitle(router.currentRoute.value);
});


const app = createApp(App);

app.provide('appInsights', appInsights)
app.use(createPinia()); // Ajoute Pinia
app.use(router);  // Utilisation du router pour la navigation
app.use(i18n);
app.component('v-chart', VueECharts)
app.mount('#app');  // L'application est montée sur l'élément DOM avec l'id 'app'
